/**
 * Maps a color name to a fully qualified Tailwind background class.
 *
 * The Headline component must never build class names dynamically
 * (e.g. `bg-${color}`), because Tailwind's source scanner cannot detect
 * such strings and purges them from the build. Callers that only know a
 * color name at runtime (CV color, route meta color, ...) route it through
 * this map so every possible class string exists literally in the source.
 *
 * https://tailwindcss.com/docs/detecting-classes-in-source-files#dynamic-class-names
 *
 * This map is the single source of truth: `HeadlineColor` is derived from its
 * keys, so adding a color here automatically widens the type — and typed call
 * sites (e.g. the CV color store) get compile-time safety for free.
 */
const HEADLINE_BG_CLASSES = {
  black: 'bg-black',
  white: 'bg-white',
  transparent: 'bg-transparent',
  orange: 'bg-orange',
  'orange-light': 'bg-orange-light',
  blue: 'bg-blue',
  'blue-light': 'bg-blue-light',
  green: 'bg-green',
  'green-light': 'bg-green-light',
  red: 'bg-red',
  pink: 'bg-pink',
  yellow: 'bg-yellow',
} as const

/** Every color name the Headline component knows how to render. */
export type HeadlineColor = keyof typeof HEADLINE_BG_CLASSES

/**
 * Visible fallback for an unmapped color, so a typo or a missing entry can
 * never render a headline with no background (invisible on a white page).
 */
const FALLBACK_BG_CLASS: string = HEADLINE_BG_CLASSES['orange-light']

/** Narrows an arbitrary (possibly untrusted) string to a known headline color. */
export function isHeadlineColor(
  color: string | null | undefined,
): color is HeadlineColor {
  return (
    !!color && Object.prototype.hasOwnProperty.call(HEADLINE_BG_CLASSES, color)
  )
}

/**
 * Resolves a (possibly untrusted) color name to a background class.
 *
 * - No color → `''`: the caller intentionally wants no background.
 * - Known color → its fully qualified class.
 * - Unknown color → a visible fallback plus a dev-only warning, so a missing
 *   mapping surfaces loudly instead of silently dropping the background.
 *   Inputs can be genuinely arbitrary at runtime (e.g. a `?color=` query param
 *   in ApplicationPortfolioView), which is why this is validated here and not
 *   only via the `HeadlineColor` type.
 */
export function headlineBgClass(color?: string | null): string {
  if (!color) {
    return ''
  }

  if (isHeadlineColor(color)) {
    return HEADLINE_BG_CLASSES[color]
  }

  if (import.meta.env.DEV) {
    console.warn(
      `[headlineBgClass] Unknown color "${color}". Add it to HEADLINE_BG_CLASSES ` +
        `in src/components/headlineBgClass.ts. Falling back to "${FALLBACK_BG_CLASS}".`,
    )
  }
  return FALLBACK_BG_CLASS
}
